import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCarSide,
  faClock,
  faEnvelope,
  faLocationDot,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

const statusOrder = {
  Arrived: 0,
  "On-Route": 1,
  Pending: 2,
};

const firstName = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)[0] || name;

const statusClass = (status) => {
  switch (status) {
    case "Arrived":
      return "bg-green-600 text-white";
    case "On-Route":
      return "bg-yellow-500 text-white";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const normalizeTime = (time) => {
  if (!time) return "99:99";
  const value = String(time).trim();
  const match = value.match(/^([0-9]{1,2}):([0-9]{2})/);
  if (!match) return value;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

const RideTracker = ({ eventParticipations }) => {
  const rides = eventParticipations
    .filter((p) => !p.isDriver && p.needRide === "Yes" && p.assignedDriverId)
    .map((p) => ({
      id: p.id,
      name: p.name,
      driverName: p.assignedDriverId?.name || "Unassigned",
      pickupLocation: p.rideLocation,
      pickupStatus: p.pickupStatus || "Pending",
      pickupTime: p.pickupTime,
      noOfPpl: p.noOfPpl || 1,
      messageForAdmin: p.messageForAdmin,
    }))
    .sort((a, b) => {
      const statusDiff =
        (statusOrder[a.pickupStatus] ?? 99) -
        (statusOrder[b.pickupStatus] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      return normalizeTime(a.pickupTime).localeCompare(
        normalizeTime(b.pickupTime),
      );
    });

  const counts = rides.reduce((acc, ride) => {
    acc[ride.pickupStatus] = (acc[ride.pickupStatus] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-lg">
      <div className="p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
              Live pickup status
            </p>
            <h3 className="text-xl font-black text-gray-900">Ride Tracker</h3>
            <p className="mt-1 text-sm text-gray-500">
              Sorted by status first, then pickup time.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-bold">
            <span className="rounded-md bg-green-50 px-2 py-1 text-green-700">
              Arrived {counts.Arrived || 0}
            </span>
            <span className="rounded-md bg-yellow-50 px-2 py-1 text-yellow-700">
              On way {counts["On-Route"] || 0}
            </span>
            <span className="rounded-md bg-gray-100 px-2 py-1 text-gray-700">
              Pending {counts.Pending || 0}
            </span>
          </div>
        </div>

        {rides.length === 0 ? (
          <p className="rounded-lg bg-gray-50 p-5 text-center text-gray-600">
            No assigned rides to track yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {rides.map((ride) => (
              <div
                key={ride.id}
                className="relative overflow-hidden rounded-lg border border-gray-100 bg-gray-50 px-3 pb-3 pt-8"
              >
                <span
                  className={`absolute left-0 top-0 rounded-br-lg px-3 py-1 text-xs font-bold ${statusClass(
                    ride.pickupStatus,
                  )}`}
                >
                  {ride.pickupStatus}
                </span>
                <span className="absolute right-0 top-0 rounded-bl-lg bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800 shadow-sm">
                  <FontAwesomeIcon icon={faCarSide} className="mr-1" />
                  {firstName(ride.driverName)}
                </span>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-900">
                      {ride.name}
                      {ride.messageForAdmin && (
                        <span
                          className="tooltip ml-2 text-[#004a7e]"
                          data-tip={ride.messageForAdmin}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </span>
                      )}
                      {ride.noOfPpl > 1 && (
                        <span className="ml-2 rounded-md bg-[#004a7e] px-2 py-0.5 text-xs text-white">
                          <FontAwesomeIcon icon={faUsers} /> {ride.noOfPpl}
                        </span>
                      )}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      <FontAwesomeIcon icon={faLocationDot} className="mr-2" />
                      {ride.pickupLocation || "No pickup location"}
                    </p>
                    {ride.pickupTime && (
                      <span className="mt-2 inline-flex rounded-md bg-white px-2 py-1 text-xs font-bold text-gray-700 shadow-sm">
                        <FontAwesomeIcon icon={faClock} className="mr-1" />
                        {ride.pickupTime}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideTracker;
